import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShoppingBag, Timer, Factory, ShieldCheck, Truck } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { HeroSlideshow } from "../components/HeroSlideshow";
import { HeroCountdown, currentWindow } from "../components/Countdown";

const STEPS = [
  { n: "01", icon: ShoppingBag, title: "Claim Your Gear", desc: "Shop Paragon Winter '26 before the ordering window closes." },
  { n: "02", icon: Timer, title: "Two Order Windows", desc: "Order by Nov 4 for delivery before Christmas. The store stays open through Dec 4; those orders will most likely arrive after Christmas." },
  { n: "03", icon: Factory, title: "Built For Paragon", desc: "Your Winter '26 gear enters team production." },
  { n: "04", icon: ShieldCheck, title: "Quality Checked", desc: "Every piece is checked before fulfillment." },
  { n: "05", icon: Truck, title: "Gear Up", desc: "Your order is packed, shipped and tracking is sent." },
];

const CATEGORIES = [
  { label: "Rash Guards", q: "category=Rash%20Guards", img: "/categories/rash-guard.jpg" },
  { label: "No-Gi", q: "category=No-Gi", img: "/categories/no-gi.jpg" },
  { label: "T-Shirts", q: "category=T-Shirts", img: "/categories/t-shirt.jpg" },
  { label: "Hoodies", q: "category=Hoodies", img: "/categories/hoodies.jpg" },
  { label: "Kickboxing", q: "category=Kickboxing", img: "/categories/kickboxing.jpg" },
  { label: "Gis", q: "category=Gis", img: "/categories/gi.jpg" },
  { label: "Youth", q: "audience=Youth", img: "/categories/youth.jpg" },
  { label: "Headwear", q: "category=Headwear", img: "/categories/headwear.jpg" },
  { label: "Accessories", q: "category=Accessories", img: "/categories/accessories.jpg" },
];

const CAT_ORDER = ["Rash Guards", "No-Gi", "T-Shirts", "Hoodies", "Kickboxing", "Gis", "Headwear", "Accessories"];

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products?round=round-1").then(({ data }) => {
      const rank = (p) => {
        let i = CAT_ORDER.indexOf(p.category);
        return i === -1 ? 99 : i;
      };
      const sorted = [...data].sort((a, b) => rank(a) - rank(b));
      setProducts(sorted.slice(0, 8));
    }).catch(() => {});
  }, []);


  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <HeroSlideshow />
        <div className="absolute inset-0 pg-hero-overlay" />
        <div className="absolute inset-0 pg-grain opacity-60" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8 w-full animate-fade-up py-16">
          <p className="font-mono text-emerald-400 uppercase tracking-[0.3em] text-[11px] sm:text-xs mb-2">
            Paragon Collection
          </p>
          <p className="font-mono text-yellow-400 uppercase tracking-[0.3em] text-xs sm:text-sm mb-4">
            Winter '26 · {currentWindow()?.round || "Order window closed"}
          </p>
          <h1 className="font-heading font-black uppercase text-white text-5xl sm:text-6xl lg:text-8xl leading-[0.85] tracking-tight">
            Built For<br />The Team.<br /><span className="text-yellow-400">Made For<br />The Mat.</span>
          </h1>
          <div className="mt-6">
            <HeroCountdown />
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/shop?round=round-1" data-testid="shop-winter-cta" className="btn-primary px-8 py-4 rounded-sm inline-flex items-center gap-2">
              Shop Winter '26 <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/gym/login" data-testid="hero-gym-login" className="btn-outline px-8 py-4 rounded-sm">
              Gym Owner Login
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Gear */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-8 mt-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="font-mono text-yellow-400 uppercase tracking-widest text-xs">Paragon Winter '26</p>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white mt-1">Featured Gear</h2>
          </div>
          <Link to="/shop?round=round-1" className="text-sm font-mono uppercase tracking-wider text-zinc-400 hover:text-yellow-400">
            View All →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* Shop By Category */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-8 mt-20">
        <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white mb-8">Shop By Category</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {CATEGORIES.map((c) => (
            <Link key={c.label} to={`/shop?${c.q}`} data-testid={`category-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="relative group h-40 md:h-56 rounded-lg overflow-hidden border border-white/10 hover:border-yellow-400/50 transition-colors">
              <img src={c.img} alt={`${c.label} - Paragon BJJ`} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-[opacity,transform] duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-5">
                <h4 className="font-heading font-black uppercase text-2xl text-white group-hover:text-yellow-400 transition-colors">{c.label}</h4>
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Shop →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* From The Drop To The Mat */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-8 mt-24">
        <div className="text-center mb-10">
          <p className="font-mono text-emerald-400 uppercase tracking-widest text-xs">The Process</p>
          <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white mt-1">From The Drop To The Mat</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {STEPS.map((s) => (
            <div key={s.n} className="bg-[#121216] border border-white/10 rounded-lg p-5 hover:border-yellow-400/40 transition-colors">
              <div className="flex items-center justify-between">
                <s.icon className="h-6 w-6 text-yellow-400" />
                <span className="font-mono text-2xl font-bold text-white/10">{s.n}</span>
              </div>
              <h4 className="font-heading font-bold uppercase text-white mt-3 text-base">{s.title}</h4>
              <p className="text-zinc-400 text-sm mt-1">{s.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-zinc-500 text-sm text-center mt-6">Once quality control is complete, your order is packed and shipped with tracking.</p>
      </section>

      {/* Wholesale (green) */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-8 mt-24">
        <div className="relative rounded-xl overflow-hidden border border-emerald-500/25">
          <img data-testid="wholesale-bg-image" src="/hero/paragon-hero-6.jpg" alt="Paragon academy mat with team gear" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-right" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/75 to-black/20" />
          <div className="relative p-10 lg:p-16 max-w-2xl">
            <p className="font-mono text-emerald-400 uppercase tracking-widest text-xs">For Academies &amp; Gym Owners</p>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-5xl text-white mt-2 leading-none">
              Rep Your Academy.<br /><span className="text-emerald-400">Wholesale Built For The Team.</span>
            </h2>
            <p className="text-zinc-300 mt-5">
              Access Paragon wholesale pricing, customize available gear with your academy branding, build bulk size
              orders, and manage everything through one centralized portal.
            </p>
            <Link to="/gym/login" data-testid="wholesale-cta" className="btn-green px-8 py-4 rounded-sm inline-flex items-center gap-2 mt-7">
              Enter Wholesale Portal <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Paragon Network CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-8 mt-16">
        <div className="bg-[#121216] border border-white/10 rounded-xl p-10 text-center">
          <p className="font-mono text-yellow-400 uppercase tracking-widest text-xs">The Paragon Network</p>
          <h2 className="font-heading font-black uppercase text-3xl lg:text-5xl text-white mt-2">One Team. Multiple Academies.</h2>
          <p className="text-zinc-400 mt-3 max-w-xl mx-auto">Find your Paragon academy and stay connected to the team.</p>
          <Link to="/academies" data-testid="academies-cta" className="btn-outline inline-block px-8 py-3.5 rounded-sm mt-6">Find Your Academy →</Link>
        </div>
      </section>

      <div className="h-8" />
    </div>
  );
}
